// src/utils/clipboardUtils.ts
import { Node } from '@xyflow/react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

const NODE_CLIPBOARD_KEY = 'spechops-node-data';
const LOCAL_STORAGE_CLIPBOARD_KEY = 'spechops-has-node-clipboard';

export const useClipboardUtils = (
  getNode: (id: string) => Node | undefined,
  screenToFlowPosition: (pos: { x: number; y: number }) => { x: number; y: number },
  addNode: (node: Node) => void
) => {
  const [hasCopiedNode, setHasCopiedNode] = useState(false);

  useEffect(() => {
    const hasStoredClipboard = localStorage.getItem(LOCAL_STORAGE_CLIPBOARD_KEY) === 'true';
    setHasCopiedNode(hasStoredClipboard);
  }, []);

  const copyNodeToClipboard = useCallback(
    (nodeId: string) => {
      const node = getNode(nodeId);
      if (!node) return;

      const nodeCopy = {
        id: node.id,
        type: node.type,
        data: node.data,
        position: { x: 0, y: 0 },
        height: node.height,
        width: node.width,
        draggable: node.draggable,
        selectable: node.selectable,
        connectable: node.connectable,
        _clipboard: {
          source: 'spechops',
          timestamp: Date.now(),
        },
      };

      try {
        const serializedNode = JSON.stringify({
          [NODE_CLIPBOARD_KEY]: nodeCopy,
        });
        navigator.clipboard.writeText(serializedNode);
        setHasCopiedNode(true);
        localStorage.setItem(LOCAL_STORAGE_CLIPBOARD_KEY, 'true');
        toast.success('Node copied to clipboard', {
          description: 'You can now paste this node in any SpecHops project.',
        });
      } catch (err) {
        toast.error('Failed to copy node', {
          description: 'Please try again or use duplicate instead.',
        });
        console.error('Failed to copy node:', err);
      }
    },
    [getNode]
  );
  const pasteNodeFromClipboard = useCallback(
    async (position: { x: number; y: number } | null = null) => {
      const isSecureContext = window.isSecureContext || window.location.hostname === 'localhost';
      if (!isSecureContext || !navigator.clipboard) {
        toast.error('Clipboard access not available', {
          description: 'Please use HTTPS or localhost for clipboard functionality.',
        });
        return;
      }

      try {
        const clipboardText = await navigator.clipboard.readText();
        let clipboardData;

        try {
          clipboardData = JSON.parse(clipboardText);
        } catch (e) {
          toast.error('No valid node data in clipboard');
          localStorage.removeItem(LOCAL_STORAGE_CLIPBOARD_KEY);
          setHasCopiedNode(false);
          return;
        }

        if (!clipboardData[NODE_CLIPBOARD_KEY]) {
          toast.error('No valid node data in clipboard');
          localStorage.removeItem(LOCAL_STORAGE_CLIPBOARD_KEY);
          setHasCopiedNode(false);
          return;
        }

        const nodeData = clipboardData[NODE_CLIPBOARD_KEY];
        const pastePosition = position || screenToFlowPosition({
          x: window.innerWidth / 2,
          y: window.innerHeight / 2,
        });

        const newNode: Node = {
          id: `${nodeData.id}-paste-${Math.floor(Math.random() * 1000)}`,
          type: nodeData.type,
          position: pastePosition,
          data: nodeData.data,
          height: nodeData.height,
          width: nodeData.width,
          draggable: nodeData.draggable,
          selectable: nodeData.selectable,
          connectable: nodeData.connectable,
        };

        addNode(newNode);
        toast.success('Node pasted from clipboard');
      } catch (err) {
        toast.error('Failed to paste node', {
          description: 'Make sure you have a valid node copied to your clipboard.',
        });
        console.error('Failed to paste node:', err);
        localStorage.removeItem(LOCAL_STORAGE_CLIPBOARD_KEY);
        setHasCopiedNode(false);
      }
    },
    [screenToFlowPosition, addNode]
  );

  return {
    hasCopiedNode,
    copyNodeToClipboard,
    pasteNodeFromClipboard,
  };
};